// What the flow shows once it is over (docs/30, #20 and #23).
//
//   sent                the submission went out (Figma 24). Test A adds the
//                       plan line, because its payment is what sent it; test B
//                       and anyone who owed nothing get it without one
//   closedAfterPayment  the plan started, but the casting call closed before
//                       the submission could go (§3); the plan is kept
//   sendFailed          paid, but the send failed; they try again from here
//   countryAfterPayment paid, but casting doesn't take their country
//                       (countryNotEligible): trying again can't help, so the
//                       exits of closedAfterPayment, the plan kept
//
// Who submitted for free (US talent, a free casting call) gets the Pro offer
// under the message, after the submission is sent and never before it.

import React from "react";
import styled from "styled-components";
import i18next from "i18next";
import { longDate } from "./dates";
import { membershipPrimary } from "./membershipButton";

export type OutcomeKind = "sent" | "closedAfterPayment" | "sendFailed" | "countryAfterPayment";
export type OutcomePlan = "trial" | "pro" | null;
export type OutcomeProOffer = "us" | "freeBreakdown" | null;

interface Props {
  kind: OutcomeKind;
  plan?: OutcomePlan;
  trialEndsUtc?: string | null;
  proOffer?: OutcomeProOffer;
  breakdownName?: string;
  onDashboard: () => void;
  onExplore: () => void;
  onSeeCastingCalls: () => void;
  onGoPro: () => void;
  onRetry?: () => void;
  retrying?: boolean;
}

const planLine = (plan: OutcomePlan, trialEndsUtc?: string | null): string => {
  if (plan === "pro") return i18next.t("Your PRO membership is now active.");
  if (plan !== "trial") return "";
  const date = longDate(trialEndsUtc);
  // No end date to hand: the line stops after "has started".
  return date
    ? i18next.t("Your free PRO trial has started. It runs until {{date}}.", { date })
    : i18next.t("Your free PRO trial has started.");
};

const offerText = (offer: OutcomeProOffer): string =>
  offer === "us"
    ? i18next.t("Stand out with PRO: up to 16 photos on your profile, and more.")
    : offer === "freeBreakdown"
    ? i18next.t(
        "Most casting calls are only for PRO members or trials. Go PRO to keep submitting."
      )
    : "";

const SubmissionOutcome: React.FC<Props> = ({
  kind,
  plan = null,
  trialEndsUtc,
  proOffer = null,
  breakdownName,
  onDashboard,
  onExplore,
  onSeeCastingCalls,
  onGoPro,
  onRetry,
  retrying = false,
}) => {
  const line = planLine(plan, trialEndsUtc);
  const offer = kind === "sent" ? offerText(proOffer) : "";

  return (
    <Centered>
      <Emoji aria-hidden="true">
        {kind === "sent" ? "🎉" : kind === "sendFailed" ? "⚠️" : "🎬"}
      </Emoji>
      <Title>
        {kind === "sent"
          ? i18next.t("Your submission has been sent!")
          : kind === "sendFailed"
          ? i18next.t("Your submission didn't go out")
          : kind === "countryAfterPayment"
          ? i18next.t("Casting for this role isn't open to your country.")
          : i18next.t("This casting call closed before your submission went out")}
      </Title>

      {line && (
        <PlanLine>
          <svg viewBox="0 0 16 16" width={16} height={16} aria-hidden="true" focusable="false">
            <circle cx="8" cy="8" r="8" fill="#1f9d55" />
            <path
              d="M4.6 8.3l2.2 2.2 4.6-4.8"
              fill="none"
              stroke="#ffffff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{line}</span>
        </PlanLine>
      )}

      <Message>
        {kind === "sent"
          ? i18next.t("Casting has your submission. Break a leg!")
          : kind === "sendFailed"
          ? i18next.t("Something went wrong. Please try again.")
          : i18next.t(
              "Your photos and resume are saved, so your next submission only takes a few clicks."
            )}
      </Message>
      {kind !== "closedAfterPayment" && kind !== "countryAfterPayment" && breakdownName && (
        <Detail>{breakdownName}</Detail>
      )}

      {offer && (
        <Offer>
          <OfferText>{offer}</OfferText>
          <OfferButton type="button" onClick={onGoPro}>
            {i18next.t("Go PRO")}
          </OfferButton>
        </Offer>
      )}

      {kind === "sendFailed" ? (
        <>
          <PrimaryButton type="button" onClick={onRetry} disabled={retrying}>
            {retrying ? i18next.t("One moment…") : i18next.t("Try again")}
          </PrimaryButton>
          <TextButton type="button" onClick={onDashboard}>
            {i18next.t("Go to my dashboard")}
          </TextButton>
        </>
      ) : kind === "closedAfterPayment" || kind === "countryAfterPayment" ? (
        <>
          <PrimaryButton type="button" onClick={onSeeCastingCalls}>
            {i18next.t("See open casting calls")}
          </PrimaryButton>
          <TextButton type="button" onClick={onDashboard}>
            {i18next.t("Go to my dashboard")}
          </TextButton>
        </>
      ) : (
        <>
          <PrimaryButton type="button" onClick={onDashboard}>
            {i18next.t("Go to my dashboard")}
          </PrimaryButton>
          <TextButton type="button" onClick={onExplore}>
            {i18next.t("Explore more opportunities!")}
          </TextButton>
        </>
      )}
    </Centered>
  );
};

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 0 8px;
`;

const Emoji = styled.div`
  font-size: 40px;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 8px;
  max-width: 340px;
  overflow-wrap: break-word;

  @media all and (min-width: 1024px) {
    max-width: 100%;
  }
`;

const PlanLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 340px;
  margin: 2px 0 10px;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #122640;
  text-align: left;

  svg {
    flex: 0 0 auto;
    margin-top: 2px;
  }
`;

const Message = styled.div`
  font-size: 14px;
  color: #53627c;
  line-height: 20px;
`;

const Detail = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #122640;
  margin-top: 10px;
`;

const Offer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  margin-top: 20px;
  padding: 14px 16px;
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  background-color: #f5f8fc;
  text-align: left;
`;

const OfferText = styled.div`
  flex: 1 1 auto;
  font-size: 14px;
  line-height: 20px;
  color: #122640;
`;

const OfferButton = styled.button`
  flex: 0 0 auto;
  background-color: #ffffff;
  color: #0f9bf0;
  border: 1px solid #0f9bf0;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 700;
  padding: 10px 16px;
  white-space: nowrap;
  cursor: pointer;
  min-height: 44px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  margin-top: 22px;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

const TextButton = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  padding: 14px 0;
  cursor: pointer;
  min-height: 44px;
`;

export default SubmissionOutcome;
